import { Button, LanguageDropdown, Layout, Link, Typography, useMobile } from '@sonnen/web-ui';
import clsx from 'clsx';
import { memo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../../AppContext/AppContext';
import { AppRoute, noBottomCtaRoutes } from '../../constants';
import { useTranslation } from '../../i18n/i18n';
import { SupportedLanguage } from '../../i18n/types';
import { useOfferContext } from '../../OfferContext/OfferContext';
import styles from './Footer.module.css';

const languages: { label: string; value: SupportedLanguage }[] = [
  { label: 'Deutsch (DE)', value: 'de' },
  { label: 'English (UK)', value: 'en-GB' },
];

const Footer = () => {
  const { t, language, changeLanguage } = useTranslation();
  const mobile = useMobile();
  const { offerRequiresManualAddress, currentBackground } = useAppContext();
  const { offer, offerLoading } = useOfferContext();
  const { pathname } = useLocation();

  const routesWithSpaceForMobileCta = mobile && !noBottomCtaRoutes.includes(pathname as AppRoute);
  const bottomSpace =
    (offer || offerLoading || offerRequiresManualAddress) && routesWithSpaceForMobileCta;

  const lightVariant = Boolean(currentBackground);
  const variant = lightVariant ? 'on-colour' : 'secondary';

  const openPrivacySettings = () => {
    if (window.UC_UI) window.UC_UI.showSecondLayer();
  };

  return (
    <footer
      className={clsx(styles.footer, {
        [styles.spaceForMobileOffer]: bottomSpace,
        [styles.backgroundFade]: lightVariant,
      })}
      data-testid="footer"
      aria-label="footer"
    >
      <Layout centerVertically backgroundColor="none">
        <div className={styles.footerItems}>
          <div className={styles.sonnen} data-testid="copyright">
            <Typography.Label2 variant={variant}>{t('footer.sonnen')}</Typography.Label2>
          </div>

          <div className={styles.language}>
            <LanguageDropdown<SupportedLanguage>
              title={t('footer.changeLanguage')}
              languages={languages}
              language={language}
              changeLanguage={changeLanguage}
              lightVariant={lightVariant}
              buttonAriaLabel={t('footer.changeLanguageButtonAriaLabel')}
              menuAriaLabel={t('footer.languageMenuAriaLabel')}
            />
          </div>

          <div
            className={clsx(styles.links, { [styles.light]: lightVariant })}
            data-testid="footer-links"
          >
            <Link href="https://sonnen.de/agb" target="_blank" dataTestId="terms-of-use-link">
              <Typography.Label2 variant={variant}>{t('footer.termsOfUse')}</Typography.Label2>
            </Link>

            <Button
              variant="tertiary"
              size="small"
              label={t('footer.privacySettings')}
              dataTestId="privacy-settings-btn"
              onClick={openPrivacySettings}
              className={clsx(styles.privacySettingsBtn)}
            />

            <Link href="https://sonnen.de/impressum" target="_blank" dataTestId="imprint">
              <Typography.Label2 variant={variant}>{t('footer.imprint')}</Typography.Label2>
            </Link>
          </div>
        </div>
      </Layout>
    </footer>
  );
};

export default memo(Footer);
